import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import React, { createElement, forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  countSideLayoutOpen,
  isFocusedAtom,
  useAtom,
  useSetAtom,
} from '~/atom';
import { IconButton } from '~/components/ui/nucleus/Button/IconButton';

type SideLayoutProps = {
  children: React.ReactNode;
  as?: React.ElementType<any>;
  /** @deprecated Use SideLayout.Head instead */
  header?: string;
  isPopup?: boolean;
  [x: string]: any;
  noScroll?: boolean;
  willOverflow?: boolean;
};

type AltProps = {
  children?: React.ReactNode;
};

type HeadProps = {
  children?: React.ReactNode;
  /** @deprecated avoid in favour of passing an action */
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  secondRightSlot?: React.ReactNode;
  onClose?: CallableFunction;
  iconClass?: 'x' | 'arrow-left';

  /**
   * @deprecated
   */
  prevUrl?: string;
};

type BodyProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: React.ReactNode;
  isPopup?: boolean;
};

type FootProps = {
  children: React.ReactNode;
  className?: string;
  show?: boolean;
  willOverflow?: boolean;
};

type Ref = HTMLDivElement;

function content(children: React.ReactNode) {
  return typeof children === 'string' ? <Body>{children}</Body> : children;
}

const Head = ({
  children,
  rightSlot,
  secondRightSlot,
  leftSlot,
  onClose,
  iconClass = 'x',
  prevUrl = '',
}: HeadProps) => {
  const renderLeft = () => {
    if (leftSlot) return leftSlot;
    if (prevUrl) return <Link to={prevUrl} className={'arrow-left'} />;
    if (onClose) {
      if (iconClass === 'arrow-left') {
        return (
          <IconButton
            size='sm'
            icon={faArrowLeft as IconDefinition}
            onClick={() => onClose()}
          />
        );
      }
      return (
        <IconButton
          size='md'
          icon={faXmark as IconDefinition}
          onClick={() => onClose()}
        />
      );
    }
  };

  return (
    <header className='flex items-center justify-between bg-white px-4 py-2'>
      <div className='h-10 w-10 text-secondary'>{renderLeft()}</div>
      <div className='mx-1 mb-4 flex items-center justify-center overflow-visible break-words pt-4 text-center text-xl font-medium leading-none text-copy-head'>
        {children ? children : <div className='h-5' />}
      </div>
      {secondRightSlot ? (
        <div className='flex items-center'>
          <div className='h-10 w-10 text-right text-sm text-secondary'>
            {secondRightSlot}
          </div>

          <div className='h-10 w-10 text-right text-sm text-secondary'>
            {rightSlot}
          </div>
        </div>
      ) : (
        <div className='flex h-10 w-10 items-center justify-center text-right text-sm text-secondary'>
          {rightSlot}
        </div>
      )}
    </header>
  );
};

const Body = forwardRef<Ref, BodyProps>(
  ({ children, isPopup, className, ...props }, ref) => (
    <div
      ref={ref}
      // Children can use this attribute to find the closest parent they should scroll
      data-autoscroll='1'
      className={classNames(
        className ?? 'px-4',
        isPopup ? 'lg:h-[651px]' : 'flex flex-grow flex-col'
      )}
      {...props}
    >
      {children}
    </div>
  )
);

const Foot = ({ children, className, show = true }: FootProps) => {
  const [isFocused] = useAtom(isFocusedAtom);
  const hasChildren = React.Children.map(children, (child) => {
    if (child?.toString !== undefined) {
      return child.toString;
    }
  });
  return (
    <Transition
      as='footer'
      show={show && !isFocused}
      className={classNames(
        hasChildren?.length && 'sticky bottom-0 z-[65] pb-6 lg:pb-4',
        'relative w-full bg-white/70 backdrop-blur-md',
        className
      )}
      enter='transition-all duration-150 ease-in-out'
      enterFrom='-bottom-40 opacity-0'
      enterTo='bottom-0 opacity-100'
      leave='transition-all duration-150 ease-in-out'
      leaveFrom='bottom-0 opacity-100'
      leaveTo='-bottom-40 opacity-0'
    >
      {children}
    </Transition>
  );
};

/**
 * Use `SideLayout` for the side panel common design elements
 */
export const SideLayout = ({
  as = 'div',
  children,
  header,
  isPopup,
  className,
  noScroll,
  willOverflow,
  ...props
}: SideLayoutProps) => {
  const setCount = useSetAtom(countSideLayoutOpen);
  useEffect(() => {
    setCount((n) => {
      if (n >= 0) document.documentElement.style.overflow = 'hidden';
      return n + 1;
    });
    return () => {
      setCount((n) => {
        if (n <= 1) document.documentElement.style.overflow = '';
        return n - 1;
      });
    };
  }, [setCount]);
  return createElement(
    as,
    {
      className: classNames(
        className,
        !isPopup &&
          'print:overflow-hidden lg:border-outline-bright lg:border-l h-screen lg:h-full bg-white',
        'z-50 lg:z-20 flex-1 w-full lg:min-w-[414px] overflow-x-hidden flex flex-col ',
        isPopup && 'min-h-0 scroll-pt-8 scroll-pb-28',
        noScroll ? 'overflow-y-hidden' : 'overflow-y-auto',
        willOverflow && 'lg:scrollbar-gutter-stable'
      ),
      ...props,
    },
    header && <div className=''>{header}</div>,
    content(children)
  );
};

export function SideLayoutAlt({ children }: AltProps) {
  return <div className='flex h-full flex-col overflow-hidden'>{children}</div>;
}

SideLayout.Head = Head;
SideLayout.Body = Body;
SideLayout.Foot = Foot;
